import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

/**
 * Custom hook to fetch and cache master data (Check Types, Clarification Types, etc.)
 * @param {string} metadataType - The type of metadata to fetch ('CHECK_TYPE', 'CLARIFICATION_TYPE', 'WORKFLOW_STATUS')
 * @param {boolean} activeOnly - Whether to fetch only active items (default: true)
 * @returns {Object} { data, loading, error, refetch }
 */
const useMasterData = (metadataType, activeOnly = true) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!metadataType) {
      setData([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const params = {
        metadataType: metadataType,
        active: activeOnly
      };
      
      const response = await api.getMasterData(params);
      const items = Array.isArray(response) ? response : [];
      
      // Sort by sortOrder, then by name
      const sortedItems = items.sort((a, b) => {
        if (a.sortOrder !== b.sortOrder) {
          return (a.sortOrder || 0) - (b.sortOrder || 0);
        }
        return (a.name || '').localeCompare(b.name || '');
      });
      
      setData(sortedItems);
    } catch (err) {
      console.error(`Error fetching master data for ${metadataType}:`, err);
      setError(err?.message || err?.data?.message || 'Failed to fetch master data');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [metadataType, activeOnly]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { 
    data, 
    loading, 
    error, 
    refetch: fetchData 
  };
};

/**
 * Helper hook specifically for Check Types
 */
export const useCheckTypes = (activeOnly = true) => {
  return useMasterData('CHECK_TYPE', activeOnly);
};

/**
 * Helper hook specifically for Clarification Types
 */
export const useClarificationTypes = (activeOnly = true) => {
  return useMasterData('CLARIFICATION_TYPE', activeOnly);
};

/**
 * Helper hook specifically for Workflow Status
 */
export const useWorkflowStatus = (activeOnly = true) => {
  return useMasterData('WORKFLOW_STATUS', activeOnly);
};

/**
 * Helper hook specifically for Batch Validation Errors
 */
export const useBatchValidationErrors = (activeOnly = true) => {
  return useMasterData('BATCH_VALIDATION_ERR', activeOnly);
};

/**
 * Bulk import (check upload) staging validation error codes
 */
export const useBulkImportErrors = (activeOnly = true) => {
  return useMasterData('BULK_IMPORT_ERR', activeOnly);
};

/**
 * Helper hook specifically for Bulk Check Update Errors
 */
export const useBulkCheckUpdateErrors = (activeOnly = true) => {
  return useMasterData('BULK_CHECK_UPDATE_ERR', activeOnly);
};

/**
 * Helper hook specifically for Bulk Batch Upload Errors
 */
export const useBulkBatchUploadErrors = (activeOnly = true) => {
  return useMasterData('BULK_BATCH_UPLOAD_ERR', activeOnly);
};

/**
 * Recon validation staging error codes (master data)
 */
export const useReconValidationErrors = (activeOnly = true) => {
  return useMasterData('RECON_VALIDATION_ERR', activeOnly);
};

export default useMasterData;
